"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.GlintCanvas = exports.Vector2 = void 0;
const default_shader_glsl_1 = __importDefault(require("./assets/shaders/default-shader.glsl"));
class Vector2 {
    constructor(x, y) {
        this.x = x;
        this.y = y;
    }
    magnitude() {
        return Math.sqrt(this.x * this.x + this.y * this.y);
    }
    toNormalized() {
        const len = this.magnitude();
        return new Vector2(this.x / len, this.y / len);
    }
    isOutOfBounds() {
        return this.x === -9999 && this.y === -9999;
    }
    moveOutofBounds() {
        this.x = -9999;
        this.y = -9999;
    }
    static from(vec2) {
        return new Vector2(vec2.x, vec2.y);
    }
}
exports.Vector2 = Vector2;
class GlintCanvas {
    constructor(options) {
        var _a, _b, _c;
        this.canvas = null;
        this.targetElement = null;
        this.gl = null;
        this.fragmentShader = null;
        this.vertexShader = null;
        this.programInfo = null;
        this.startTime = performance.now();
        this.fragmentSource = null;
        this.isInitialized = false;
        this.renderRequested = false;
        this.isRendering = false;
        this.isDestroyed = false;
        this.doPause = false;
        this.doPulse = false;
        this.doPulseGrow = true;
        this.clickTimer = 0.0;
        this.pulseSpeed = 1.0;
        this.mousePosition = new Vector2(-9999, -9999);
        /** Whether this instance currently holds a reference on the shared tracker */
        this.pointerTracked = false;
        this.clickPosition = new Vector2(-9999, -9999);
        this.options = null;
        this.scale = null;
        this.zIndex = -1;
        /** Cached canvas bounding client rect */
        this.domRectCache = null;
        this.pulseEasing = undefined;
        this.additionalUniforms = [];
        this.animationFrameId = null;
        this.positionBuffer = null;
        this.resizeObserver = null;
        this.intersectionObserver = null;
        this.maxFps = 60;
        this.maxDpr = 2;
        this.webglVersion = null;
        this.activeVersion = null;
        this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        this.onScroll = () => {
            this.domRectCache = this.canvas.getBoundingClientRect();
        };
        this.onClick = () => {
            // The window-level capture listener already recorded this pointerdown, so
            // projecting here gives the pulse a correct position even on a touch tap,
            // where no pointermove ever precedes it.
            this.projectPointer();
            this.doPulseGrow = true;
            this.doPulse = true;
            this.clickPosition = Vector2.from(this.mousePosition);
        };
        this.onResize = () => {
            let displayW;
            let displayH;
            if (this.targetElement) {
                const bounds = this.targetElement.getBoundingClientRect();
                displayW = Math.floor(bounds.width * this.dpr);
                displayH = Math.floor(bounds.height * this.dpr);
            }
            else {
                displayW = Math.floor(this.canvas.clientWidth * this.dpr);
                displayH = Math.floor(this.canvas.clientHeight * this.dpr);
            }
            if (this.canvas.width !== displayW || this.canvas.height !== displayH) {
                this.canvas.width = displayW;
                this.canvas.height = displayH;
                this.gl.viewport(0, 0, displayW, displayH);
            }
            this.domRectCache = this.canvas.getBoundingClientRect();
        };
        /** Hover state only - the mouse uniform is derived from geometry, see projectPointer */
        this.onMouseEnter = () => {
            var _a, _b;
            this.canvas.classList.add("hover");
            (_b = (_a = this.options).onHover) === null || _b === void 0 ? void 0 : _b.call(_a, this);
        };
        this.onMouseLeave = () => {
            this.canvas.classList.remove("hover");
        };
        this.options = options;
        this.canvas = options.element;
        this.webglVersion = (_a = options.webglVersion) !== null && _a !== void 0 ? _a : "webgl";
        this.targetElement = (_b = this.options.targetElement) !== null && _b !== void 0 ? _b : null;
        const observedElement = (_c = this.targetElement) !== null && _c !== void 0 ? _c : this.canvas;
        if (observedElement === document.body) {
            this.init();
            this.startLoop();
            return;
        }
        this.intersectionObserver = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                this.init();
                if (this.renderRequested) {
                    this.startLoop();
                }
                return;
            }
            this.deactivate();
        }, { rootMargin: "100px" });
        this.intersectionObserver.observe(observedElement);
    }
    attachPointerTracker() {
        if (this.pointerTracked)
            return;
        this.pointerTracked = true;
        if (GlintCanvas.pointerTrackerRefs++ > 0)
            return;
        window.addEventListener("pointermove", GlintCanvas.trackPointer, { passive: true, capture: true });
        window.addEventListener("pointerdown", GlintCanvas.trackPointer, { passive: true, capture: true });
        document.addEventListener("pointerleave", GlintCanvas.clearPointer);
    }
    detachPointerTracker() {
        if (!this.pointerTracked)
            return;
        this.pointerTracked = false;
        if (--GlintCanvas.pointerTrackerRefs > 0)
            return;
        window.removeEventListener("pointermove", GlintCanvas.trackPointer, { capture: true });
        window.removeEventListener("pointerdown", GlintCanvas.trackPointer, { capture: true });
        document.removeEventListener("pointerleave", GlintCanvas.clearPointer);
    }
    /** WebGL version of the active context, or `null` while not initialized. */
    get version() {
        return this.activeVersion;
    }
    createContext() {
        const candidates = this.webglVersion === "auto"
            ? ["webgl2", "webgl"]
            : [this.webglVersion];
        for (const version of candidates) {
            const context = this.canvas.getContext(version);
            if (!context)
                continue;
            if (context.isContextLost()) {
                // Externally lost (GPU reset, too many live contexts). drawScene would
                // bail out every frame and render nothing at all, so say so out loud.
                console.warn(`GlintCanvas: the "${version}" context of this canvas is lost and cannot be restored. Rendering stays blank until the canvas is recreated.`);
            }
            this.activeVersion = version;
            return context;
        }
        this.activeVersion = null;
        return null;
    }
    init() {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        if (this.isInitialized)
            return;
        this.gl = this.createContext();
        if (!this.gl) {
            throw new Error(`WebGL not supported (requested version: "${this.webglVersion}")`);
        }
        let fps = (_a = this.options.fps) !== null && _a !== void 0 ? _a : 60;
        let mobileFps = (_b = this.options.mobileFps) !== null && _b !== void 0 ? _b : 30;
        this.maxFps = this.isMobile ? mobileFps : fps;
        this.maxDpr = (_c = this.options.maxPixelRatio) !== null && _c !== void 0 ? _c : 2;
        this.isInitialized = true;
        this.isDestroyed = false;
        this.doPulse = false;
        this.doPulseGrow = false;
        this.clickTimer = 0;
        this.clickPosition.moveOutofBounds();
        this.mousePosition.moveOutofBounds();
        this.attachPointerTracker();
        this.pulseSpeed = (_d = this.options.pulseSpeed) !== null && _d !== void 0 ? _d : 1;
        this.fragmentSource = (_e = this.options.fragmentSource) !== null && _e !== void 0 ? _e : default_shader_glsl_1.default;
        if (!this.options.fragmentSource) {
            console.warn("No fragment shader supplied. Using default.");
        }
        if (this.isGlsl3 && this.activeVersion !== "webgl2") {
            throw new Error(`Fragment shader declares "#version 300 es" but the active context is WebGL 1. Set webglVersion to "webgl2" or "auto".`);
        }
        this.pulseEasing = (_f = this.options.pulseEasingOverride) !== null && _f !== void 0 ? _f : this.easeInOutQuart;
        this.scale = (_g = this.options.scaleModifier) !== null && _g !== void 0 ? _g : new Vector2(1, 1);
        this.zIndex = (_h = this.options.zIndex) !== null && _h !== void 0 ? _h : -1;
        const shaderProgram = this.initShaderProgram();
        this.programInfo = {
            program: shaderProgram,
            attribLocations: {
                vertexPosition: this.gl.getAttribLocation(shaderProgram, 'aVertexPosition'),
            },
            uniformLocations: {
                resolution: this.gl.getUniformLocation(shaderProgram, 'uResolution'),
                time: this.gl.getUniformLocation(shaderProgram, "uTime"),
                mouse: this.gl.getUniformLocation(shaderProgram, "uMouse"),
                pulse: this.gl.getUniformLocation(shaderProgram, "uPulse"),
                pulsePos: this.gl.getUniformLocation(shaderProgram, "uPulsePos"),
                scale: this.gl.getUniformLocation(shaderProgram, "uScale")
            },
        };
        if (this.options.uniforms) {
            this.additionalUniforms = this.options.uniforms;
            for (const uniform of this.additionalUniforms) {
                this.programInfo.uniformLocations[uniform.name] = this.gl.getUniformLocation(shaderProgram, uniform.name);
            }
        }
        if (this.targetElement) {
            this.targetElement.style.position = "relative";
            this.canvas.style.position = "absolute";
            this.canvas.style.inset = "0";
            this.canvas.style.width = "100%";
            this.canvas.style.height = "100%";
            this.canvas.style.margin = "auto";
            this.canvas.style.borderRadius = "inherit";
            this.canvas.style.zIndex = this.zIndex.toString();
            this.domRectCache = this.canvas.getBoundingClientRect();
            this.targetElement.appendChild(this.canvas);
            this.targetElement.addEventListener("pointerenter", this.onMouseEnter);
            this.targetElement.addEventListener("pointerleave", this.onMouseLeave);
            this.targetElement.addEventListener("pointerdown", this.onClick);
            window.addEventListener("scroll", this.onScroll, { passive: true, capture: true });
            window.addEventListener("resize", this.onResize);
            this.resizeObserver = new ResizeObserver(() => this.onResize());
            this.resizeObserver.observe(this.targetElement);
            this.initBuffers();
            this.onResize();
            this.onScroll();
            return;
        }
        this.domRectCache = this.canvas.getBoundingClientRect();
        this.initBuffers();
        this.onResize();
        this.onScroll();
        window.addEventListener("scroll", this.onScroll, { passive: true, capture: true });
        window.addEventListener("resize", this.onResize);
        this.canvas.addEventListener("pointerenter", this.onMouseEnter);
        this.canvas.addEventListener("pointerleave", this.onMouseLeave);
        this.canvas.addEventListener("pointerdown", this.onClick);
    }
    startRender() {
        this.renderRequested = true;
        if (this.isInitialized) {
            this.startLoop();
        }
    }
    startLoop() {
        if (this.isRendering)
            return;
        if (!this.isInitialized || !this.gl || !this.programInfo)
            return;
        this.isRendering = true;
        const frameDuration = 1000 / this.maxFps;
        let lastFrameTime = 0;
        const loop = (now) => {
            var _a, _b;
            if (!this.isRendering)
                return;
            if (!this.isInitialized || !this.gl || !this.programInfo) {
                this.isRendering = false;
                return;
            }
            if (!this.doPause) {
                const delta = now - lastFrameTime;
                if (now - lastFrameTime >= frameDuration) {
                    const time = (now - this.startTime) / 1000;
                    lastFrameTime = now;
                    (_b = (_a = this.options).onUpdate) === null || _b === void 0 ? void 0 : _b.call(_a, this, time, delta / 1000);
                    this.drawScene(time, delta);
                }
            }
            this.animationFrameId = requestAnimationFrame(loop);
        };
        this.animationFrameId = requestAnimationFrame(loop);
    }
    deactivate() {
        var _a, _b, _c, _d, _e;
        if (!this.isInitialized)
            return;
        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
        this.isRendering = false;
        (_a = this.resizeObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
        this.resizeObserver = null;
        window.removeEventListener("scroll", this.onScroll, { capture: true });
        window.removeEventListener("resize", this.onResize);
        this.detachPointerTracker();
        this.canvas.removeEventListener("pointerenter", this.onMouseEnter);
        this.canvas.removeEventListener("pointerleave", this.onMouseLeave);
        this.canvas.removeEventListener("pointerdown", this.onClick);
        (_b = this.targetElement) === null || _b === void 0 ? void 0 : _b.removeEventListener("pointerdown", this.onClick);
        (_c = this.targetElement) === null || _c === void 0 ? void 0 : _c.removeEventListener("pointerenter", this.onMouseEnter);
        (_d = this.targetElement) === null || _d === void 0 ? void 0 : _d.removeEventListener("pointerleave", this.onMouseLeave);
        if (this.gl) {
            if (this.positionBuffer) {
                this.gl.deleteBuffer(this.positionBuffer);
                this.positionBuffer = null;
            }
            if ((_e = this.programInfo) === null || _e === void 0 ? void 0 : _e.program) {
                this.gl.deleteProgram(this.programInfo.program);
            }
            if (this.vertexShader) {
                this.gl.deleteShader(this.vertexShader);
                this.vertexShader = null;
            }
            if (this.fragmentShader) {
                this.gl.deleteShader(this.fragmentShader);
                this.fragmentShader = null;
            }
            // Losing the context returns it to the browser's context budget, but it can
            // never be re-acquired from the same canvas element - getContext() keeps
            // handing back the lost one. Only do it when a fresh canvas replaces this
            // one; a caller-supplied canvas without a targetElement has to keep its
            // context so the next init() can reuse it.
            if (this.targetElement) {
                const loseContext = this.gl.getExtension("WEBGL_lose_context");
                loseContext === null || loseContext === void 0 ? void 0 : loseContext.loseContext();
                this.replaceCanvasAfterContextLoss();
            }
        }
        this.programInfo = null;
        this.gl = null;
        this.activeVersion = null;
        this.isInitialized = false;
    }
    destroy() {
        var _a, _b;
        if (this.isDestroyed)
            return;
        this.isDestroyed = true;
        (_a = this.intersectionObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
        this.intersectionObserver = null;
        this.deactivate();
        if (this.canvas.parentElement === this.targetElement) {
            (_b = this.targetElement) === null || _b === void 0 ? void 0 : _b.removeChild(this.canvas);
        }
    }
    replaceCanvasAfterContextLoss() {
        if (!this.targetElement)
            return;
        const oldCanvas = this.canvas;
        const newCanvas = oldCanvas.cloneNode(false);
        oldCanvas.replaceWith(newCanvas);
        this.canvas = newCanvas;
        this.canvas.style.position = "absolute";
        this.canvas.style.inset = "0";
        this.canvas.style.width = "100%";
        this.canvas.style.height = "100%";
        this.canvas.style.margin = "auto";
        this.canvas.style.borderRadius = "inherit";
        this.canvas.style.zIndex = this.zIndex.toString();
    }
    pause() {
        var _a, _b;
        this.doPause = true;
        (_b = (_a = this.options).onPause) === null || _b === void 0 ? void 0 : _b.call(_a, this);
    }
    unpause() {
        var _a, _b;
        this.doPause = false;
        (_b = (_a = this.options).onUnpause) === null || _b === void 0 ? void 0 : _b.call(_a, this);
    }
    /**
     * Projects the shared pointer position into canvas pixel space, or moves the mouse
     * out of bounds while the pointer is not over this canvas.
     *
     * Deliberately derived from geometry rather than from pointerenter/pointerleave:
     * boundary events are hit-tested asynchronously and are not emitted at all when the
     * canvas moves under a stationary pointer. They therefore cannot answer "is the
     * pointer over me" while the page scrolls, nor after a deactivate/init cycle.
     */
    projectPointer() {
        const pointer = GlintCanvas.pointerClient;
        const rect = this.domRectCache;
        if (!pointer || !rect
            || pointer.x < rect.left || pointer.x > rect.right
            || pointer.y < rect.top || pointer.y > rect.bottom) {
            this.mousePosition.moveOutofBounds();
            return;
        }
        const x = (pointer.x - rect.left) * this.dpr;
        const y = (rect.height - (pointer.y - rect.top)) * this.dpr;
        this.mousePosition = new Vector2(x, y);
    }
    drawScene(time, delta) {
        if (!this.gl || !this.programInfo || !this.positionBuffer)
            return;
        if (this.gl.isContextLost()) {
            return;
        }
        this.projectPointer();
        const numComponents = 2;
        const type = this.gl.FLOAT;
        const normalize = false;
        const stride = 0;
        const offset = 0;
        this.gl.vertexAttribPointer(this.programInfo.attribLocations.vertexPosition, numComponents, type, normalize, stride, offset);
        this.gl.enableVertexAttribArray(this.programInfo.attribLocations.vertexPosition);
        this.gl.useProgram(this.programInfo.program);
        this.updateUniforms(time, delta);
    }
    updateUniforms(time, delta) {
        if (this.doPulse && this.doPulseGrow) {
            this.clickTimer += (delta / 1000) * this.pulseSpeed;
            if (this.clickTimer >= 1.) {
                this.doPulseGrow = false;
            }
        }
        else if (this.doPulse && !this.doPulseGrow) {
            this.clickTimer -= (delta / 1000) * this.pulseSpeed;
            if (this.clickTimer <= 0) {
                this.doPulse = false;
                this.doPulseGrow = true;
                this.clickTimer = 0;
                this.clickPosition.moveOutofBounds();
            }
        }
        this.gl.uniform2f(this.programInfo.uniformLocations.pulsePos, this.clickPosition.x, this.clickPosition.y);
        this.gl.uniform1f(this.programInfo.uniformLocations.pulse, this.pulseEasing(this.clickTimer));
        this.gl.uniform1f(this.programInfo.uniformLocations.time, time);
        this.gl.uniform3f(this.programInfo.uniformLocations.resolution, this.canvas.width, this.canvas.height, 1.0);
        this.gl.uniform2f(this.programInfo.uniformLocations.scale, this.scale.x, this.scale.y);
        this.gl.uniform2f(this.programInfo.uniformLocations.mouse, this.mousePosition.x, this.mousePosition.y);
        for (const uniform of this.additionalUniforms) {
            this.setCustomUniform(uniform);
        }
        const offset = 0;
        const vertexCount = 4;
        this.gl.drawArrays(this.gl.TRIANGLE_STRIP, offset, vertexCount);
    }
    setCustomUniform(uniform) {
        const name = uniform.name;
        const value = uniform.value;
        if (value instanceof Vector2) {
            this.gl.uniform2f(this.programInfo.uniformLocations[name], value.x, value.y);
            return;
        }
        if (typeof value === "number") {
            this.gl.uniform1f(this.programInfo.uniformLocations[name], value);
        }
    }
    initBuffers() {
        this.positionBuffer = this.gl.createBuffer();
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.positionBuffer);
        const positions = [
            1.0, 1.0,
            -1.0, 1.0,
            1.0, -1.0,
            -1.0, -1.0,
        ];
        this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array(positions), this.gl.STATIC_DRAW);
    }
    initShaderProgram() {
        this.vertexShader = this.loadShader(this.gl, this.gl.VERTEX_SHADER, this.vertexSource);
        this.fragmentShader = this.loadShader(this.gl, this.gl.FRAGMENT_SHADER, this.fragmentSource);
        const shaderProgram = this.gl.createProgram();
        this.gl.attachShader(shaderProgram, this.vertexShader);
        this.gl.attachShader(shaderProgram, this.fragmentShader);
        this.gl.linkProgram(shaderProgram);
        if (!this.gl.getProgramParameter(shaderProgram, this.gl.LINK_STATUS)) {
            throw new Error('Unable to initialize the shader program: ' + this.gl.getProgramInfoLog(shaderProgram));
        }
        return shaderProgram;
    }
    loadShader(gl, type, source) {
        const shader = gl.createShader(type);
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            // Read the log first: a deleted shader raises INVALID_VALUE and returns null.
            const log = gl.getShaderInfoLog(shader);
            gl.deleteShader(shader);
            const stage = type === gl.VERTEX_SHADER ? "vertex" : "fragment";
            throw new Error(`An error occurred compiling the ${stage} shader (${this.activeVersion}, GLSL ES ${this.isGlsl3 ? "3.00" : "1.00"}): ${log}`);
        }
        return shader;
    }
    get dpr() {
        return Math.min(window.devicePixelRatio || 1, this.maxDpr);
    }
    /**
     * Default easing function. From https://easings.net
     */
    easeInOutQuart(x) {
        return x < 0.5 ? 8 * x * x * x * x : 1 - Math.pow(-2 * x + 2, 4) / 2;
    }
    /** True when the fragment source declares GLSL ES 3.00. */
    get isGlsl3() {
        var _a;
        return /^\s*#version\s+300\s+es\b/.test((_a = this.fragmentSource) !== null && _a !== void 0 ? _a : "");
    }
    /** Vertex shader matching the GLSL version of the fragment source.
     * Both stages have to declare the same version or linking fails.
     */
    get vertexSource() {
        // No leading whitespace: #version has to be the first token of the source.
        if (this.isGlsl3) {
            return `#version 300 es
                in vec4 aVertexPosition;
                void main() {
                    gl_Position = aVertexPosition;
                }`;
        }
        return `attribute vec4 aVertexPosition;
                void main() {
                    gl_Position = aVertexPosition;
                }`;
    }
}
exports.GlintCanvas = GlintCanvas;
/**
 * Viewport position of the pointer, shared by all instances. There is only one
 * physical pointer, and every instance needs to know where it is even while it is
 * outside its own bounds. Null until the first pointer event of the page, and
 * again once the pointer leaves the document.
 */
GlintCanvas.pointerClient = null;
GlintCanvas.pointerTrackerRefs = 0;
GlintCanvas.trackPointer = (e) => {
    GlintCanvas.pointerClient = new Vector2(e.clientX, e.clientY);
};
GlintCanvas.clearPointer = () => {
    GlintCanvas.pointerClient = null;
};
